import React from 'react'
import {
  PiCheckCircle,
  PiInfo,
  PiLightbulb,
  PiWarning,
  PiWarningCircle,
} from 'react-icons/pi'
import { cn } from 'components/UI/cn'

export type AlertColor = 'success' | 'warning' | 'info' | 'danger' | 'default'

interface AlertProps {
  color?: AlertColor
  /** Optional bold heading. When set, renders the "With icon and title"
   *  Figma variant (p-4, gap-2, md/semibold heading + sm/regular body).
   *  Without it, renders the simpler single-line variant (px-4 py-2,
   *  gap-3, sm/medium body). */
  title?: string
  description: React.ReactNode
  className?: string
  /** Override the auto-picked icon if needed (defaults: success→CheckCircle,
   *  warning→Warning, info→Lightbulb, danger→WarningCircle, default→Info). */
  icon?: React.ComponentType<{ size?: number; className?: string }>
}

const ICON_FOR_COLOR: Record<
  AlertColor,
  React.ComponentType<{ size?: number; className?: string }>
> = {
  success: PiCheckCircle,
  warning: PiWarning,
  info: PiLightbulb,
  danger: PiWarningCircle,
  default: PiInfo,
}

const CONTAINER_BY_COLOR: Record<AlertColor, string> = {
  success: 'bg-alert-success-bg border-alert-success-border',
  warning: 'bg-alert-warning-bg border-alert-warning-border',
  info: 'bg-alert-info-bg border-alert-info-border',
  danger: 'bg-alert-danger-bg border-alert-danger-border',
  default: 'bg-alert-default-bg border-alert-default-border',
}

const TITLE_TEXT_BY_COLOR: Record<AlertColor, string> = {
  success: 'text-alert-success-title',
  warning: 'text-alert-warning-title',
  info: 'text-alert-info-title',
  danger: 'text-alert-danger-title',
  default: 'text-alert-default-title',
}

const BODY_TEXT_BY_COLOR: Record<AlertColor, string> = {
  success: 'text-alert-success-text',
  warning: 'text-alert-warning-text',
  info: 'text-alert-info-text',
  danger: 'text-alert-danger-text',
  default: 'text-alert-default-text',
}

/**
 * DS2026 alert. Mirrors the Figma "Alerts" component (node 7172:1687):
 * - Without `title`: single-line "With icon" variant — px-4 py-2, gap-3,
 *   sm/medium body text colored with the title token.
 * - With `title`: "With icon and title" variant — p-4, gap-2, md/semibold
 *   heading on top, sm/regular description below.
 */
export function Alert({
  color = 'default',
  title,
  description,
  className,
  icon,
}: AlertProps) {
  const Icon = icon ?? ICON_FOR_COLOR[color]
  const hasTitle = title != null

  // Danger alerts use role="alert" (assertive — interrupts screen readers
  // immediately for errors). Other colors use role="status" (polite — read
  // when the user is idle).
  const ariaRole = color === 'danger' ? 'alert' : 'status'

  return (
    <div
      role={ariaRole}
      className={cn(
        'flex items-start gap-3 rounded-lg border tracking-[-0.2px]',
        CONTAINER_BY_COLOR[color],
        hasTitle ? 'p-4' : 'px-4 py-2 items-center',
        className
      )}
    >
      <Icon size={24} className={cn('shrink-0', TITLE_TEXT_BY_COLOR[color])} />
      {hasTitle ? (
        <div className="flex min-w-0 flex-col gap-2">
          <p
            className={cn(
              'text-base font-semibold leading-6',
              TITLE_TEXT_BY_COLOR[color]
            )}
          >
            {title}
          </p>
          <div
            className={cn(
              'text-sm font-normal leading-5',
              BODY_TEXT_BY_COLOR[color]
            )}
          >
            {description}
          </div>
        </div>
      ) : (
        <div
          className={cn(
            'min-w-0 flex-1 text-sm font-medium leading-5',
            TITLE_TEXT_BY_COLOR[color]
          )}
        >
          {description}
        </div>
      )}
    </div>
  )
}
